// Shared "the data isn't here" surfaces.
//
// Several pages had no isError branch, so a failed fetch rendered as an
// empty table or a row of em-dashes — the console quietly claiming the
// cluster has no workers when in fact the request 500'd. These two
// components make the distinction explicit and give every failure a retry.
//
// Both wrap @veil/ui's Empty so the shape (icon, title, description,
// action) matches the empty states Fleet and Uploads already use.

import { Button } from "@veil/ui/components/button";
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@veil/ui/components/empty";
import { cn } from "@veil/ui/lib/utils";
import type { LucideIcon } from "lucide-react";
import Inbox from "lucide-react/dist/esm/icons/inbox";
import RotateCw from "lucide-react/dist/esm/icons/rotate-cw";
import TriangleAlert from "lucide-react/dist/esm/icons/triangle-alert";
import type { ReactNode } from "react";

export interface QueryErrorProps {
	/** What failed, in the operator's terms: "Couldn't load workers". */
	title?: string;
	error: unknown;
	onRetry?: () => void;
	/** Pending state of the retry, so the button can't be double-fired. */
	retrying?: boolean;
	className?: string;
}

export function errorMessage(error: unknown): string {
	if (error instanceof Error && error.message) return error.message;
	if (typeof error === "string" && error.length > 0) return error;
	return "Unknown error";
}

/**
 * Failed-query state. `role="alert"` so the failure is announced rather
 * than sitting silently where content used to be.
 */
export function QueryError({
	title = "Couldn't load this data",
	error,
	onRetry,
	retrying = false,
	className,
}: QueryErrorProps) {
	return (
		<Empty
			role="alert"
			className={cn("border border-destructive/30 bg-destructive/5", className)}
		>
			<EmptyHeader>
				<EmptyMedia variant="icon" className="text-destructive">
					<TriangleAlert />
				</EmptyMedia>
				<EmptyTitle>{title}</EmptyTitle>
				<EmptyDescription className="font-mono text-xs break-all">
					{errorMessage(error)}
				</EmptyDescription>
			</EmptyHeader>
			{onRetry ? (
				<EmptyContent>
					<Button
						variant="outline"
						size="sm"
						onClick={onRetry}
						disabled={retrying}
					>
						<RotateCw className={cn(retrying && "animate-spin")} />
						{retrying ? "Retrying…" : "Retry"}
					</Button>
				</EmptyContent>
			) : null}
		</Empty>
	);
}

export interface EmptyStateProps {
	icon?: LucideIcon;
	title: string;
	description?: ReactNode;
	/** Usually the one thing to do next: "Publish a build". */
	action?: ReactNode;
	className?: string;
}

/** Genuinely-no-data state. Always says what to do next when there is one. */
export function EmptyState({
	icon: Icon = Inbox,
	title,
	description,
	action,
	className,
}: EmptyStateProps) {
	return (
		<Empty className={cn("border", className)}>
			<EmptyHeader>
				<EmptyMedia variant="icon">
					<Icon />
				</EmptyMedia>
				<EmptyTitle>{title}</EmptyTitle>
				{description ? (
					<EmptyDescription>{description}</EmptyDescription>
				) : null}
			</EmptyHeader>
			{action ? <EmptyContent>{action}</EmptyContent> : null}
		</Empty>
	);
}
